import { SessionDeadLink } from '@/components/session/dead-link';
import { SessionPlayer } from '@/components/session/session-player';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { ApiError } from '@/lib/api';
import {
  NO_NOYAU_WAIT,
  type NoyauWait,
  isNoyauWaiting,
  noyauWaitPressed,
  noyauWaitRefused,
  noyauWaitSettled,
} from '@/lib/noyau-wait';
import { advanceSession, fetchSession, sendMessage, sessionEventsUrl } from '@/lib/session-api';
import {
  BUBBLE_GRACE_MS,
  BUBBLE_STALL_MS,
  IDLE_CONVERSATION,
  conversationLive,
  sessionThread,
  threadPageOf,
} from '@/lib/session-conversation';
import { sessionRefusalSentence } from '@/lib/session-labels';
import { useElapsed } from '@/lib/use-elapsed';
import { useReaderFormat } from '@/lib/use-reader-format';
import type { SessionAdvance, SessionEvent, SessionView } from '@matter_extractor/shared';
import { sessionEventSchema } from '@matter_extractor/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useReducer, useRef, useState } from 'react';

/**
 * `/s/<jeton>` - the SESSION (§5.2, §5.3, §9.2 to §9.5).
 *
 * « `/s/...` prend toute la fenêtre et est peinte par le thème de
 * l'expérience : rien de PMS n'y subsiste, sinon le thème ne servirait à
 * rien. » So this route mounts no shell of its own - `__root.tsx` already steps
 * aside for anything that paints its own window - and everything on screen
 * comes out of `SessionPlayer`, which spends the theme's own tokens.
 *
 * ## Three channels, and only one of them writes
 *
 * The REST read is the source of truth and is re-read on every mount AND on
 * every connection of the channel, the two writes are POSTs - the advance of a
 * page, the message of §9.5 - and the SSE channel carries DELTAS. The doctrine
 * of the chat, and what the contract #50 wrote down: « le client relit la
 * session par REST à chaque connexion et le canal ne porte que des deltas ». A
 * frame lost to a dropped connection therefore costs a re-read and never a
 * bubble.
 *
 * ## Why the conversation's state lives HERE
 *
 * The channel is opened for the whole session and not for the protocol page: a
 * `status` frame is what tells a tab sitting on a content page that another one
 * closed the session. The frames of the conversation arrive on that same
 * connection, so the thread is reduced here and handed down - the component
 * that draws bubbles is given a thread, and owns nothing but the draft in its
 * field.
 *
 * What a frame does is decided by TYPE and never by « something happened »:
 * only a `status` triggers a re-read. The version of #60 invalidated on every
 * frame, which was right when the channel carried statuses alone and would now
 * fire one refetch per WORD of the interviewer.
 *
 * ## What this route does NOT do
 *
 * It never decides an ending. `expired` is derived by the server from the start
 * (§5.3) and arrives in the view; the page draws what it is told, so a tab left
 * open across the 24 hours learns it at its next read rather than from a timer
 * of its own that could disagree with the server refusing the advance.
 */
export const Route = createFileRoute('/s/$token')({
  component: SessionRoute,
});

function SessionRoute() {
  const { token } = Route.useParams();
  return <SessionPage token={token} />;
}

/** What a failure the screen has no French for reads as, for both writes. */
const GENERIC_REFUSAL = 'Cette réponse n’a pas pu être enregistrée. Réessayez dans un instant.';

export interface SessionPageProps {
  /** The session's token, read off the address by the route. */
  readonly token: string;
}

/**
 * EXPORTED for `tests/session-page.test.tsx`, at the régime of
 * `ReglagesNoyauxPage`: `vitest.config.ts` leaves `src/routes/**` out of the
 * coverage REPORT, not out of the suite, and what this page wires - the wait at
 * the button between the advance and the channel - is invisible from the
 * components below it and from the pure machine alike.
 */
export function SessionPage({ token }: SessionPageProps) {
  const queryClient = useQueryClient();
  const format = useReaderFormat();

  const session = useQuery({
    queryKey: ['session', token],
    queryFn: () => fetchSession(token),
    retry: false,
    // The door of `/l/<secret>` has just read this very key to decide what to
    // offer; arriving here on that cached answer would replay a stale page.
    refetchOnMount: 'always',
  });

  const advance = useMutation({
    mutationFn: (body: SessionAdvance) => advanceSession(token, body),
    onSuccess: (view: SessionView) => {
      // A re-read still in flight - a status frame, a reconnection - may have
      // been answered by the server BEFORE this advance and land AFTER it: it
      // would put back the page the person just left, and they would press it
      // a second time. It is cancelled first, which reverts the query at once
      // and drops the late answer, then the advance's view - the newest - is
      // written.
      void queryClient.cancelQueries({ queryKey: ['session', token] });
      queryClient.setQueryData(['session', token], view);
    },
  });

  /*
    THE WAIT AT THE BUTTON (noyau.md §4.6, N5): an advance refused
    `noyau-pending` is HELD, the button pressed keeps saying its waiting label -
    « Un instant... » on « Continuer », a Bouton's own since #174 - and the
    advance is replayed at the `noyau` frame of the channel - or at once, when
    the frame beat the refusal (`noyau-wait.ts`). The machine lives in a ref,
    because the channel's listener is registered once and has to read its
    latest state; `waiting` is the one bit of it the screen draws, and with the
    request in flight it makes `advancing`, which holds from the press through
    the REPLAY until an answer lands - otherwise the pressed button would flash
    back to its label between the refusal and the frame.
  */
  const wait = useRef<NoyauWait>(NO_NOYAU_WAIT);
  const [waiting, setWaiting] = useState(false);
  const post = useRef<(body: SessionAdvance) => void>(() => undefined);
  useEffect(() => {
    post.current = (body: SessionAdvance): void => {
      wait.current = noyauWaitPressed();
      advance.mutate(body, {
        onSuccess: () => setWaiting(false),
        onError: (error: unknown) => {
          if (error instanceof ApiError && refusalCode(error) === 'feedback-already-sent') {
            // The feedback was already sent - from another tab, or by a press
            // this one did not see land (retour-experience.md §10): the session
            // is re-read, and it shows the phrase of thanks.
            setWaiting(false);
            void queryClient.invalidateQueries({ queryKey: ['session', token] });
            return;
          }
          if (!(error instanceof ApiError) || refusalCode(error) !== 'noyau-pending') {
            setWaiting(false);
            return;
          }
          const step = noyauWaitRefused(wait.current, body);
          wait.current = step.wait;
          if (step.replay !== null) {
            post.current(step.replay);
            return;
          }
          setWaiting(isNoyauWaiting(step.wait));
        },
      });
    };
  });

  const [live, dispatch] = useReducer(conversationLive, IDLE_CONVERSATION);

  /*
    The SSE channel of §9.5 - a SUBSCRIPTION and not a fetch, which is why it is
    an effect: the rule of this repository is about loading data with
    `useEffect`, and the load is the `useQuery` above.

    Every frame is PARSED (`sessionEventSchema`) before it is believed - a
    server response is an external input even when we wrote the server - and one
    the union refuses is dropped silently, which is what lets an older tab
    survive a newer server.

    `open` fires on the first connection AND on every reconnection the browser
    makes on its own, and it is the whole catch-up: the tail of the thread is
    dropped and the session re-read. What a status frame triggers is a REFETCH
    and never a write into the cache: the frame carries a stored status, the
    view carries a derived one plus a page and its values, and patching half a
    view from a delta is how two sources of truth start.
  */
  useEffect(() => {
    if (typeof EventSource === 'undefined') return;
    const source = new EventSource(sessionEventsUrl(token));
    const reread = (): void => {
      void queryClient.invalidateQueries({ queryKey: ['session', token] });
    };
    // A re-read the pages of the thread ask for: once it has landed, the tail
    // sheds the messages of the protocol the floor now holds (`caught-up`). Only
    // once it has LANDED: `invalidateQueries` resolves on a failed refetch too,
    // and shedding the tail onto a floor that did not move would take the last
    // bubbles off the screen until the next re-read.
    const rereadThread = (): void => {
      void queryClient.invalidateQueries({ queryKey: ['session', token] }).then(() => {
        if (queryClient.getQueryState(['session', token])?.status === 'success') {
          dispatch({ kind: 'caught-up' });
        }
      });
    };
    const onOpen = (): void => {
      dispatch({ kind: 'reconnected' });
      reread();
    };
    const onMessage = (event: MessageEvent<string>): void => {
      const frame = safeFrame(event.data);
      if (frame === null) return;
      dispatch({ kind: 'frame', event: frame });
      if (frame.type === 'status') reread();
      /*
        The pages played in the thread (prolongation.md §4, issue #186): the
        SERVER moves the session there - on the protocol's last answer, after a
        noyau that failed - so the view this tab holds can name a page that is no
        longer the one. A frame that says the session is on a page of the thread
        re-reads: the entry in the thread (`protocol: page`), the bubble of a page
        this tab does not stand on, and the determination settling under a page
        whose cards were awaited (the cards are in the view, or the page was
        skipped). The tail's bubbles the floor then holds are drawn once, keyed
        by their page (`conversationThread`).
      */
      const held = queryClient.getQueryData<SessionView>(['session', token]);
      const onThread =
        held !== undefined && threadPageOf(held.content.pages, held.currentPageId) !== null;
      if (frame.type === 'protocol' && frame.state === 'page') rereadThread();
      // A protocol `closed` while this tab draws a page of the thread re-reads too:
      // the server may have left the page for an ordinary one (a noyau that did
      // not come), and that phase is the one frame that says so. A PLAIN re-read,
      // the tail kept: the goodbye that closes the thread arrives on this channel
      // before the click's own answer can land, and that answer - which does not
      // hold it - cancels the re-read and writes its view. Shed onto it, the
      // goodbye would leave the screen; kept, it is drawn once (`alreadyRead`).
      if (frame.type === 'protocol' && frame.state === 'closed' && onThread) reread();
      if (frame.type === 'bubble' && held?.currentPageId !== frame.bubble.pageId) rereadThread();
      if (frame.type === 'noyau' && onThread) rereadThread();
      if (frame.type === 'noyau') {
        // The determination is settled: the held advance is replayed, and a
        // frame that beat its refusal is remembered (noyau.md §4.6).
        const step = noyauWaitSettled(wait.current);
        wait.current = step.wait;
        if (step.replay !== null) post.current(step.replay);
      }
    };
    source.addEventListener('open', onOpen);
    source.addEventListener('message', onMessage);
    return () => {
      source.removeEventListener('open', onOpen);
      source.removeEventListener('message', onMessage);
      source.close();
    };
  }, [token, queryClient]);

  /*
    The message of §9.5. It answers 202 with no body, so there is nothing to put
    in the cache: what the person sees next comes off the channel.

    A refusal is worded from its CODE and never from the server's sentence, and
    `session-over` is re-read as well as worded - it is what a session past its
    24 hours answers, and only the view can draw the sober screen of §5.3.
  */
  const message = useMutation({
    mutationFn: (text: string) => sendMessage(token, text),
    onError: (error: unknown) => {
      const code = error instanceof ApiError ? refusalCode(error) : null;
      dispatch({ kind: 'refused', code });
      if (code === 'session-over') {
        void queryClient.invalidateQueries({ queryKey: ['session', token] });
      }
    },
  });

  const send = async (text: string): Promise<boolean> => {
    dispatch({ kind: 'sent', text, at: new Date().toISOString() });
    try {
      await message.mutateAsync(text);
      return true;
    } catch {
      // Already worded by `onError`; the component only needs to know whether
      // to put the person's words back in the field.
      return false;
    }
  };

  /*
    The pages of the thread wait for their bubble, and nothing on the channel
    says it will never come: past `BUBBLE_GRACE_MS` without a word of it, the
    elements come alone (prolongation.md §4.6). Computed BEFORE the early
    returns - a hook - from a view that may not be there yet.
  */
  const probe = session.data === undefined ? undefined : sessionThread(session.data, live, false);
  const idle = probe?.withheld === true && !live.typing && live.streamingId === null;
  const pageKey = session.data?.currentPageId ?? '';
  // And a ceiling the stream does not reset: a bubble whose writing stopped with no
  // frame at all - an engine that threw after `typing` - must not keep the person
  // either. Past the seat's own budget, the server would have sent an empty one.
  const stalled = useElapsed(probe?.withheld === true, pageKey, BUBBLE_STALL_MS);
  const gaveUp = useElapsed(idle, pageKey, BUBBLE_GRACE_MS) || stalled;

  if (session.isError) {
    // A token that resolves nothing, a session in the trash: one answer, and it
    // is the same dead screen the door shows - « pas d'oracle » (§5.1).
    if (session.error instanceof ApiError && session.error.status === 404) {
      return <SessionDeadLink />;
    }
    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <p className="max-w-[62ch] text-center text-base text-gray-700">
          La conversation est momentanément injoignable. Rechargez cette page dans un instant.
        </p>
      </div>
    );
  }

  if (session.isPending) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <PmsSpinner label="Ouverture de la conversation" />
      </div>
    );
  }

  // `noyau-pending` is not a refusal to the person: it is the wait itself, and
  // the button already says it (its waiting label, « Un instant... » by default).
  const heldOnTheNoyau =
    advance.error instanceof ApiError && refusalCode(advance.error) === 'noyau-pending';
  // Nor is `feedback-already-sent`: the re-read above answers it with the thanks.
  const alreadySent =
    advance.error instanceof ApiError && refusalCode(advance.error) === 'feedback-already-sent';
  const refusal = heldOnTheNoyau || alreadySent ? null : refusalOf(advance.error, advance.isError);

  const threadOf = sessionThread(session.data, live, gaveUp);

  return (
    <SessionPlayer
      view={session.data}
      format={format}
      advancing={advance.isPending || waiting}
      refusal={refusal}
      onAdvance={(body) => post.current(body)}
      conversation={{
        thread: threadOf.thread,
        typing: live.typing,
        state: threadOf.state,
        busy: live.sending,
        refusal: refusalOf(message.error, message.isError),
        onSend: send,
      }}
    />
  );
}

/** A frame of the channel, or null - an unknown `type` is dropped, never thrown on. */
function safeFrame(data: string): SessionEvent | null {
  try {
    const parsed = sessionEventSchema.safeParse(JSON.parse(data));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** The `code` a business refusal carries, out of `ApiError.details`. */
function refusalCode(error: ApiError): string | null {
  const details = error.details;
  if (typeof details !== 'object' || details === null) return null;
  const code = (details as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

/** One sentence for one failed write, from its code when there is one. */
function refusalOf(error: unknown, failed: boolean): string | null {
  if (error instanceof ApiError) {
    return sessionRefusalSentence(refusalCode(error)) ?? GENERIC_REFUSAL;
  }
  return failed ? GENERIC_REFUSAL : null;
}
